
import * as THREE from 'three';
import { gsap } from "gsap";
import Experience from '../Experience.js';
import Resources from '../Utils/Resources.js';
import Data from '../Utils/Data.js';

import CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';

import ballVertexShader from "../Shaders/ball/ball_vertex.glsl";
import ballFragmentShader from "../Shaders/ball/ball_fragment.glsl";
import wobbleVertexShader from '../Shaders/ball/wobble_vertex.glsl'
import wobbleFragmentShader from '../Shaders/ball/wobble_fragment.glsl'


export default class Body {
    constructor() {
        this.experience = Experience.getInstance();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.renderer = this.experience.renderer.instance;
        this.debug = this.experience.debug;
        this.sizes = this.experience.sizes;
        this.resources = Resources.getInstance();
        this.data = Data.getInstance();

        this.textureLoader = new THREE.TextureLoader();

        this.createBall();
        this.createWobblyMaterial();
        this.breathAnimation();
    }

    createBall() {
        // this.sphereGeometry = new THREE.SphereGeometry(this.data.sphereRadius, 32, 32);
        this.sphereGeometry = new THREE.IcosahedronGeometry(this.data.sphereRadius, 50);
        this.sphereGeometry = mergeVertices(this.sphereGeometry);
        this.sphereGeometry.computeTangents();

        this.sphereMaterial = new THREE.ShaderMaterial({
            vertexShader: ballVertexShader,
            fragmentShader: ballFragmentShader,
            transparent: true,
            uniforms: {
                uColor: { value: this.data.hairColors.base },
                uTime: new THREE.Uniform(0),
                uHologram: new THREE.Uniform(false),
                uExplosion: new THREE.Uniform(false),
            },
        });

        this.sphere = new THREE.Mesh(this.sphereGeometry, this.sphereMaterial);
        this.sphere.receiveShadow = true
        this.sphere.castShadow = true
        this.scene.add(this.sphere);

    }
    createWobblyMaterial() {

        this.csmUniforms = {
            uTime: new THREE.Uniform(0),
            uPositionFrequency: new THREE.Uniform(0.8),
            uTimeFrequency: new THREE.Uniform(0.4),
            uStrength: new THREE.Uniform(0.2),
            uWarpPositionFrequency: new THREE.Uniform(0.53),
            uWarpTimeFrequency: new THREE.Uniform(0.12),
            uWarpStrength: new THREE.Uniform(1.7),
            uColorA: new THREE.Uniform(new THREE.Color('#091902')),
            uColorB: new THREE.Uniform(new THREE.Color('#2e7a0b'))
        }

        this.wobblySphereMaterial = new CustomShaderMaterial({
            // CSM
            baseMaterial: THREE.MeshPhysicalMaterial,
            vertexShader: wobbleVertexShader,
            fragmentShader: wobbleFragmentShader,
            uniforms: this.csmUniforms,
            silent: true,

            // MeshPhysicalMaterial
            metalness: 0,
            roughness: 0.5,
            color: '#ffffff',
            transmission: 0,
            ior: 1.5,
            thickness: 1.5,
            transparent: true,
            wireframe: false
        })


        this.wobblyDepthMaterial = new CustomShaderMaterial({
            // CSM
            baseMaterial: THREE.MeshDepthMaterial,
            vertexShader: wobbleVertexShader,
            uniforms: this.csmUniforms,
            silent: true,

            // MeshDepthMaterial
            depthPacking: THREE.RGBADepthPacking
        })

        // this.sphere.material = this.wobblySphereMaterial;
        // this.sphere.customDepthMaterial = this.wobblyDepthMaterial;
        //this.sphere.scale.setScalar(1);
    }

    breathAnimation() {
        //Breathing
        this.breathe = gsap.to(this.sphere.scale, {
            y: 0.98, //squeeze
            x: 0.98,
            z: 0.98,
            duration: 0.6,
            yoyo: true, // Go back to original shape
            repeat: -1, // Loop the animation
            ease: "sine.inOut"
        });
        // this.breathe.timeScale(newSpeed); // Apply speed change
        // console.log(this.breathe.vars)
    }

    changeBreathing(newSpeed) {
        this.breathe.timeScale(newSpeed);
    }

    forwardAnimation() {
        gsap.fromTo(
            this.sphere.position,
            { z: 0 }, // Start position (far away)
            { z: 2.4, duration: 0.3, ease: "power2.in" } // Animate to center fast
        );
        // gsap.fromTo(
        //     this.sphere.scale, 
        //     { x: 0.8, y: 0.8, z: 0.8 }, // Start tiny
        //     { x: 1.5, y: 1.5, z: 1.5, duration: 0.3, ease: "power2.out" } // Scale up
        // );
    }

    tiltAnimation() {
        gsap.fromTo(
            this.sphere.rotation,
            { z: 0 }, // Start tilted left
            { z: -0.2, duration: 0.3, ease: "power2.out" }
        );
    }

    updateBodyColor(newColor) {
        this.sphereMaterial.uniforms.uColor.value = newColor;
    }

    updateBodyAlpha(alpha) {
        if (alpha < 0.2)
            this.sphereMaterial.uniforms.uHologram.value = true;
        else
            this.sphereMaterial.uniforms.uHologram.value = false;

        if (alpha > 0.95) {
            this.sphere.material = this.wobblySphereMaterial;
            this.sphere.customDepthMaterial = this.wobblyDepthMaterial;
            this.breathe.kill(); // Stop GSAP breathing animation
            this.sphere.scale.setScalar(1.2); // Set new scale manually
        }
        else {
            if (this.sphere.scale.x == 1.2) {
                this.sphere.material = this.sphereMaterial;
                this.breathe.kill(); // Stop GSAP breathing animation
                this.sphere.scale.setScalar(1);
                this.breathAnimation();
            }
        }
    }

    explode() {
        this.sphereMaterial.uniforms.uHologram.value = false;
        this.sphereMaterial.uniforms.uExplosion.value = true;
    }

    reset() {
        this.sphere.position.set(0, 0, 0);
        this.sphere.rotation.z = 0;
        this.sphere.visible = true;

        this.sphereMaterial.uniforms.uHologram.value = false;
        this.sphereMaterial.uniforms.uExplosion.value = false;

        this.sphere.material = this.sphereMaterial;
        this.breathe.kill(); // Stop GSAP breathing animation
        this.sphere.scale.setScalar(1);
        this.breathAnimation();
    }

    update() {
        this.sphereMaterial.uniforms.uTime.value = this.time.elapsed * 0.005; // Smooth animation
        this.wobblySphereMaterial.uniforms.uTime.value = this.time.elapsed * 0.003;
    }

    resize() {

    }
}